import React, { useState, useEffect } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import { Layers, ChevronDown, ChevronRight, Map } from "lucide-react";
import "../datasets/Datasets.css";

const AssignDatasetToStudyModal = ({ dataset, studies, onClose, onSuccess }) => {
    const [selectedStudies, setSelectedStudies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [annotations, setAnnotations] = useState([]);
    const [loadingAnnotations, setLoadingAnnotations] = useState(false);
    const [selectedAnnotations, setSelectedAnnotations] = useState([]);
    const [showAnnotations, setShowAnnotations] = useState(false);
    const [maps, setMaps] = useState([]);
    const [loadingMaps, setLoadingMaps] = useState(false);
    const [selectedMaps, setSelectedMaps] = useState([]);
    const [showMaps, setShowMaps] = useState(false);

    // Fetch annotations when modal opens
    useEffect(() => {
        const fetchAnnotations = async () => {
            if (!dataset?.id) return;
            setLoadingAnnotations(true);
            try {
                const response = await API.get(`/datasets/${dataset.id}/annotations/`);
                setAnnotations(response.data || []);
                // By default, all annotations are selected (included)
                setSelectedAnnotations(response.data.map(a => a.id));
            } catch (err) {
                console.error("Error fetching annotations:", err);
            } finally {
                setLoadingAnnotations(false);
            }
        };
        fetchAnnotations();
    }, [dataset?.id]);

    // Fetch maps when modal opens
    useEffect(() => {
        const fetchMaps = async () => {
            if (!dataset?.id) return;
            setLoadingMaps(true);
            try {
                const response = await API.get(`/datasets/${dataset.id}/maps/`);
                setMaps(response.data || []);
                setSelectedMaps(response.data.map(m => m.id));
            } catch (err) {
                console.error("Error fetching maps:", err);
            } finally {
                setLoadingMaps(false);
            }
        };
        fetchMaps();
    }, [dataset?.id]);

    const handleCheckboxChange = (studyId) => {
        setSelectedStudies((prev) => {
            if (prev.includes(studyId)) {
                return prev.filter((id) => id !== studyId);
            } else {
                return [...prev, studyId];
            }
        });
    };

    const handleAnnotationToggle = (annotationId) => {
        setSelectedAnnotations((prev) => {
            if (prev.includes(annotationId)) {
                return prev.filter((id) => id !== annotationId);
            } else {
                return [...prev, annotationId];
            }
        });
    };

    const handleSelectAllAnnotations = () => {
        setSelectedAnnotations(annotations.map(a => a.id));
    };

    const handleDeselectAllAnnotations = () => {
        setSelectedAnnotations([]);
    };

    const handleMapToggle = (mapId) => {
        setSelectedMaps((prev) => {
            if (prev.includes(mapId)) {
                return prev.filter((id) => id !== mapId);
            } else {
                return [...prev, mapId];
            }
        });
    };

    const handleSelectAllMaps = () => {
        setSelectedMaps(maps.map(m => m.id));
    };

    const handleDeselectAllMaps = () => {
        setSelectedMaps([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedStudies.length === 0) {
            toast.warning("Please select at least one study");
            return;
        }

        setLoading(true);
        try {
            // Calculate which annotation and map IDs to exclude
            const excludeAnnotationIds = annotations
                .filter(a => !selectedAnnotations.includes(a.id))
                .map(a => a.id);
            const excludeMapIds = maps
                .filter(m => !selectedMaps.includes(m.id))
                .map(m => m.id);
            const allExcludedIds = [...excludeAnnotationIds, ...excludeMapIds];

            // Assign dataset to each selected study
            const promises = selectedStudies.map((studyId) =>
                API.post(`/studies/${studyId}/add_datasets/`, {
                    dataset_ids: [dataset.id],
                    exclude_annotation_ids: allExcludedIds,
                })
            );

            await Promise.all(promises);
            toast.success(`Dataset assigned to ${selectedStudies.length} study(ies)`);
            onSuccess();
        } catch (err) {
            console.error("Error assigning dataset to studies:", err);
            toast.error("Failed to assign dataset to some studies.");
        } finally {
            setLoading(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            draft: "bg-secondary",
            active: "bg-primary",
            completed: "bg-success",
            archived: "bg-dark",
        };
        return <span className={`badge ${colors[status] || "bg-secondary"} ms-2`}>{status}</span>;
    };

    return (
        <div className="modal-backdrop-custom" onClick={handleBackdropClick}>
            <div className="modal-content-custom" style={{ maxWidth: "600px" }}>
                <div className="modal-header-custom">
                    <h3>Assign to Study</h3>
                    <button className="modal-close-btn" onClick={onClose}>
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body-custom">
                        <p className="text-muted mb-3">
                            Select which studies should include <strong>{dataset.name}</strong>
                        </p>

                        {studies.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                <i className="fa fa-folder-open mb-2" style={{ fontSize: "2rem", opacity: 0.5 }}></i>
                                <p>No studies in this project yet.</p>
                                <small>Create a study first before assigning datasets.</small>
                            </div>
                        ) : (
                            <div className="study-checkbox-list">
                                {studies.map((study) => (
                                    <div
                                        key={study.id}
                                        className={`study-checkbox-item ${selectedStudies.includes(study.id) ? "selected" : ""}`}
                                        onClick={() => handleCheckboxChange(study.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedStudies.includes(study.id)}
                                            onChange={() => handleCheckboxChange(study.id)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className="study-checkbox-info">
                                            <div className="study-checkbox-name">
                                                {study.name}
                                                {getStatusBadge(study.status)}
                                            </div>
                                            <div className="study-checkbox-meta">
                                                {study.description || "No description"}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedStudies.length > 0 && (
                            <div className="mt-3 p-3 bg-light rounded">
                                <small className="text-muted">
                                    <i className="fa fa-check-circle me-2 text-success"></i>
                                    {selectedStudies.length} study(ies) selected
                                </small>
                            </div>
                        )}

                        {/* Annotation Selection Section */}
                        {annotations.length > 0 && (
                            <div className="mt-4">
                                <div
                                    className="annotation-section-header"
                                    onClick={() => setShowAnnotations(!showAnnotations)}
                                    style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.75rem',
                                        background: 'rgba(156, 39, 176, 0.05)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(156, 39, 176, 0.2)'
                                    }}
                                >
                                    {showAnnotations ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    <Layers size={18} style={{ color: '#9c27b0' }} />
                                    <span style={{ fontWeight: 500 }}>Annotations</span>
                                    <span className="badge" style={{ background: '#9c27b0', color: 'white', marginLeft: 'auto' }}>
                                        {selectedAnnotations.length}/{annotations.length}
                                    </span>
                                </div>

                                {showAnnotations && (
                                    <div className="annotation-selection-content" style={{ marginTop: '0.75rem' }}>
                                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleSelectAllAnnotations}
                                            >
                                                Select All
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleDeselectAllAnnotations}
                                            >
                                                Deselect All
                                            </button>
                                        </div>

                                        <div className="annotation-list" style={{
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '6px'
                                        }}>
                                            {annotations.map((annotation) => (
                                                <div
                                                    key={annotation.id}
                                                    className={`annotation-item ${selectedAnnotations.includes(annotation.id) ? 'selected' : ''}`}
                                                    onClick={() => handleAnnotationToggle(annotation.id)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.75rem',
                                                        padding: '0.5rem 0.75rem',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid #f0f0f0',
                                                        background: selectedAnnotations.includes(annotation.id)
                                                            ? 'rgba(156, 39, 176, 0.08)'
                                                            : 'transparent'
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedAnnotations.includes(annotation.id)}
                                                        onChange={() => handleAnnotationToggle(annotation.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <Layers size={14} style={{ color: '#9c27b0' }} />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                                            {annotation.filename || `Annotation ${annotation.id}`}
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                                            Patient: {annotation.patient_id}
                                                            {annotation.annotation_type && ` • ${annotation.annotation_type}`}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6c757d' }}>
                                            <i className="fa fa-info-circle me-1"></i>
                                            Unchecked annotations will be excluded from the study
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Maps Selection Section */}
                        {maps.length > 0 && (
                            <div className="mt-3">
                                <div
                                    className="maps-section-header"
                                    onClick={() => setShowMaps(!showMaps)}
                                    style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.75rem',
                                        background: 'rgba(42, 157, 143, 0.05)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(42, 157, 143, 0.2)'
                                    }}
                                >
                                    {showMaps ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    <Map size={18} style={{ color: '#2a9d8f' }} />
                                    <span style={{ fontWeight: 500 }}>Maps</span>
                                    <span className="badge" style={{ background: '#2a9d8f', color: 'white', marginLeft: 'auto' }}>
                                        {selectedMaps.length}/{maps.length}
                                    </span>
                                </div>

                                {showMaps && (
                                    <div className="maps-selection-content" style={{ marginTop: '0.75rem' }}>
                                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleSelectAllMaps}
                                            >
                                                Select All
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleDeselectAllMaps}
                                            >
                                                Deselect All
                                            </button>
                                        </div>

                                        <div className="maps-list" style={{
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '6px'
                                        }}>
                                            {maps.map((mapItem) => (
                                                <div
                                                    key={mapItem.id}
                                                    onClick={() => handleMapToggle(mapItem.id)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.75rem',
                                                        padding: '0.5rem 0.75rem',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid #f0f0f0',
                                                        background: selectedMaps.includes(mapItem.id)
                                                            ? 'rgba(42, 157, 143, 0.08)'
                                                            : 'transparent'
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedMaps.includes(mapItem.id)}
                                                        onChange={() => handleMapToggle(mapItem.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <Map size={14} style={{ color: '#2a9d8f' }} />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                                            {mapItem.filename || `Map ${mapItem.id}`}
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                                            Patient: {mapItem.patient_id}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6c757d' }}>
                                            <i className="fa fa-info-circle me-1"></i>
                                            Unchecked maps will be excluded from the study
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer-custom">
                        <button
                            type="button"
                            className="btn-secondary-custom"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary-custom"
                            disabled={loading || studies.length === 0}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Assigning...
                                </>
                            ) : (
                                `Assign to ${selectedStudies.length || 0} Study(ies)`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignDatasetToStudyModal;
